// @flow
// https://docs.google.com/document/d/10sCWxdd1y2V-y_GgyVjYPwf1r0iKXzilkO9CUzkt_5o/edit#
// Should be able to:
//  - start tracking a node
//  - stop tracking a node
//  - flush all nodes
//  - resume tracking paused nodes
// If it's already in viewport it should start immediately
// It should start tracking when intersection is > 0 (1px on screen)
// It should stop tracking when intersection is 0
// It should flush when the page is blurred, closed, backgrounded, etc
// It should handle fixed element obstructions (headers, footers, etc)
import ImpressionObserver from 'app/packages/impression-logging/ImpressionObserver';
import type {
  Change,
  Observer,
  MutationObserver,
  MutationRecords,
} from 'app/packages/impression-logging/types';

export const HIGH_PRIORITY: boolean = true;
export const LOW_PRIORITY: boolean = false;

export default class Impressions {
  constructor() {
    this.activeImpressions = new Map();
    this.mutationObservers = new Map();
    this.bottomHeight = 0;
    this.bottomObstructions = new Set();
    // Pausing can be high or low priority.
    // Most pausing should be high priority; typically only the tab/window gaining/losing focus should be low priority.
    // This prevents over-eager resuming when an obstruction (e.g. modal) is still present.
    // See bug MQTT-580 for more context.
    this.pausePriority = LOW_PRIORITY;
    if (!__SERVER__) {
      this.observer = new window.IntersectionObserver(this._delegateChange);
    }
    this.paused = false;
    this.topHeight = 0;
    this.topObstructions = new Set();
    this.inExperiment = false;
  }

  activeImpressions: Map<HTMLElement, ImpressionObserver>;
  mutationObservers: Map<MutationObserver, HTMLElement>;
  bottomHeight: number;
  bottomObstructions: Set<HTMLElement>;
  observer: Observer;
  paused: boolean;
  pausePriority: boolean;
  topHeight: number;
  topObstructions: Set<HTMLElement>;
  inExperiment: boolean;

  setExperimentStatus(newStatus: boolean) {
    if (this.inExperiment !== newStatus) {
      this.inExperiment = newStatus;
    }
  }
  // Should be called when node is removed from DOM
  stop(node: HTMLElement, reason: string = '') {
    const impression = this.activeImpressions.get(node);
    if (impression) {
      impression.stop(reason);
      if (this.inExperiment) {
        this.mutationObservers.delete(impression.mutationObserver);
        impression.stopMutationObserver();
      }
      this.activeImpressions.delete(node);
      this.observer.unobserve(node);
    }
  }

  // Should be called when node is added to DOM
  start(node: HTMLElement) {
    let impression = this.activeImpressions.get(node);
    if (!impression) {
      impression = new ImpressionObserver(node);
      this.activeImpressions.set(node, impression);
      this.observer.observe(node);
      if (this.inExperiment) {
        //each impression object needs its own mutation observer (they can only observe one item)
        //had some funky scope issues, making sure the callback keeps the right context
        const mutationCallback = (records, observer) => {
          const self = this;
          return self._handleMutations(records, observer);
        };
        impression.setMutationObserver(new window.MutationObserver(mutationCallback));
        this.mutationObservers.set(impression.mutationObserver, node);
        impression.startMutationObserver({
          subtree: true, // the node that's targeted might be a child of the node we're given
          attributes: true,
          attributeFilter: ['style'], //we can add more filters here if anything other than style changes
        });
      }
    }
    return impression;
  }

  // Should be called when:
  //   1. modals/overlays are added to DOM
  //   2. user switches tab
  //   3. user closes browser
  // See note re: priority at this.pausePriority definition in constructor
  pause(pausePriority?: boolean = HIGH_PRIORITY) {
    if (!this.paused) {
      Array.from(this.activeImpressions.values()).forEach((impression) => impression.pause());
      this.paused = true;
      if (this.pausePriority === LOW_PRIORITY) {
        this.pausePriority = pausePriority;
      }
    }
  }

  // Should be called when pause has finished
  // See note re: priority at this.pausePriority definition in constructor
  resume(resumePriority?: boolean = HIGH_PRIORITY) {
    if (resumePriority === LOW_PRIORITY && this.pausePriority === HIGH_PRIORITY) {
      return;
    }
    if (this.paused) {
      Array.from(this.activeImpressions.values()).forEach((impression) => impression.resume());
      this.paused = false;
      this.pausePriority = LOW_PRIORITY;
    }
  }

  // Should be called when fixed content is added to DOM
  addObstruction(position: 'top' | 'bottom', node: HTMLElement) {
    if (position === 'top') {
      this.topObstructions.add(node);
    } else if (position === 'bottom') {
      this.bottomObstructions.add(node);
    }
    this._calculateRootMargins();
  }

  // Should be called when fixed content is removed from DOM
  removeObstruction(position: 'top' | 'bottom', node: HTMLElement) {
    if (position === 'top') {
      this.topObstructions.delete(node);
    } else if (position === 'bottom') {
      this.bottomObstructions.delete(node);
    }
    this._calculateRootMargins();
  }

  updateObstructions() {
    this._calculateRootMargins();
  }

  // Create a new observer bounding box based on top/bottom obstructions
  _calculateRootMargins() {
    const newTopHeight = Array.from(this.topObstructions).reduce((lowerBound: number, node) => {
      const { bottom } = node.getBoundingClientRect();
      return bottom > lowerBound ? bottom : lowerBound;
    }, 0);

    const newBottomHeight =
      window.innerHeight -
      Array.from(this.bottomObstructions).reduce((upperBound: number, node) => {
        const { top } = node.getBoundingClientRect();
        return top < upperBound ? top : upperBound;
      }, window.innerHeight);

    if (newTopHeight !== this.topHeight || newBottomHeight !== this.bottomHeight) {
      // Set observer bounds
      const options = {
        rootMargin: `${-newTopHeight}px 0px ${-newBottomHeight}px`,
      };
      // Kill old observer
      this.observer.disconnect();
      // Create new one
      this.observer = new window.IntersectionObserver(this._delegateChange, options);
      // Reattach all active impressions
      Array.from(this.activeImpressions.values()).forEach((impression) =>
        this.observer.observe(impression.node),
      );
      this.topHeight = newTopHeight;
      this.bottomHeight = newBottomHeight;
    }
  }

  // Handle change in specific impression observer
  _delegateChange = (changes: Array<Change>) => {
    changes.forEach((change: Change) => {
      const observer = this.activeImpressions.get(change.target);
      if (observer) {
        observer.handleIntersectionChange(change);
      }
    });
  };

  _handleMutations = (records: MutationRecords, observer: MutationObserver) => {
    // offsetHeight of 0 is a good indicator of the element being display:none'd
    const targetNode = this.mutationObservers.get(observer);
    const hasNoHeight = targetNode && targetNode.offsetHeight < 1;
    if (hasNoHeight && targetNode) {
      //stop the impressions for this node
      this.stop(targetNode, 'removed');
    }
  };
}
